import { Children, useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from './cn';

/**
 * Ant Design `Carousel` replacement — a horizontal slide carousel with dots,
 * optional arrows and autoplay. Each child is one slide. Respects
 * prefers-reduced-motion (no autoplay).
 */
export interface CarouselProps {
  children?: ReactNode;
  autoplay?: boolean;
  autoplaySpeed?: number;
  dots?: boolean;
  arrows?: boolean;
  draggable?: boolean;
  adaptiveHeight?: boolean;
  className?: string;
  style?: CSSProperties;
}

export function Carousel({
  children, autoplay, autoplaySpeed = 3000, dots = true, arrows, className, style,
}: CarouselProps) {
  const slides = Children.toArray(children);
  const n = slides.length;
  const [i, setI] = useState(0);
  const go = (idx: number) => setI(((idx % n) + n) % n);

  const reduce = typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  useEffect(() => {
    if (!autoplay || n <= 1 || reduce) return;
    const t = setInterval(() => setI((c) => (c + 1) % n), autoplaySpeed);
    return () => clearInterval(t);
  }, [autoplay, autoplaySpeed, n, reduce]);

  if (n === 0) return null;

  return (
    <div className={cn('relative overflow-hidden rounded-[inherit]', className)} style={style}>
      <div
        className="flex transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]"
        style={{ transform: `translateX(-${i * 100}%)` }}
      >
        {slides.map((s, idx) => (
          <div key={idx} className="w-full shrink-0">{s}</div>
        ))}
      </div>

      {arrows && n > 1 && (
        <>
          <button type="button" aria-label="Previous slide" onClick={() => go(i - 1)} className="absolute left-2 top-1/2 z-10 grid size-8 -translate-y-1/2 place-items-center rounded-full bg-black/35 text-white backdrop-blur transition hover:bg-black/55">
            <ChevronLeft className="size-5" />
          </button>
          <button type="button" aria-label="Next slide" onClick={() => go(i + 1)} className="absolute right-2 top-1/2 z-10 grid size-8 -translate-y-1/2 place-items-center rounded-full bg-black/35 text-white backdrop-blur transition hover:bg-black/55">
            <ChevronRight className="size-5" />
          </button>
        </>
      )}

      {dots && n > 1 && (
        <div className="absolute inset-x-0 bottom-2 z-10 flex items-center justify-center gap-1.5">
          {slides.map((_, idx) => (
            <button
              key={idx}
              type="button"
              aria-label={`Go to slide ${idx + 1}`}
              onClick={() => go(idx)}
              className={cn('h-1.5 rounded-full transition-all', idx === i ? 'w-5 bg-surface' : 'w-1.5 bg-white/50 hover:bg-white/80')}
            />
          ))}
        </div>
      )}
    </div>
  );
}
